import type { ApiClientDeps } from './client';
import { jsonInit, parseApiResponse } from './client';
import type { ApiResponse } from './types';

export interface PayTypeMetadata {
  editable: boolean;
  lockedFields: string[];
  copyable: boolean;
  deactivatableViaOverlay: boolean;
  retirable: boolean;
  effectiveStatus: string;
}

export interface TenantPayTypeRow {
  payTypeId: string;
  payTypeCode: string;
  payTypeName: string;
  payComponentType: string;
  groupLabel?: string;
  status: string;
  effectiveStatus: string;
  effectiveStartDate: string;
  effectiveEndDate?: string;
  belongsToPayTypeList: boolean;
  regionCodes: string[];
  isCustom: boolean;
  sourceAdminPayTypeId?: string;
  sourceAdminPayTypeCode?: string;
  calculationMethod?: string;
  factor?: number | null;
  rateTypeId?: string | null;
  rateBaseReference?: string | null;
  rateCategoryReference?: string | null;
  fixedAmount?: number | null;
  cap?: number | null;
  fixedAmountFrequency?: string | null;
  percentageValue?: number | null;
  percentageType?: string | null;
  payTypeListId?: string | null;
  annualRateReference?: string | null;
  taxIndicator?: string;
  payrollTaxCategory?: string;
  super?: {
    isOte: boolean;
    qeInclusive: boolean;
    liabilityInclusive: boolean;
    oteInclusive: boolean;
    resc: boolean;
    superPayElementType?: string | null;
  };
  stp?: {
    stp1IncomeStatementCategory?: string | null;
    stpIncomeType?: string | null;
    stpPaymentType?: string | null;
    stpAllowanceCode?: string | null;
    stpAllowanceSubCode?: string | null;
    stpPaidLeaveCode?: string | null;
    stpSalarySacrificeCode?: string | null;
    stpLumpSumCode?: string | null;
    stpEtpCode?: string | null;
    stpDeductionCode?: string | null;
    stpPaymentTypeLocked: boolean;
  };
  metadata: PayTypeMetadata;
}

export interface TenantPayTypeDetail extends TenantPayTypeRow {
  childSupport?: {
    protectedEarningType?: string | null;
    isChildSupportGarnishee: boolean;
    isChildSupportDeduction: boolean;
    deductionFrequency?: string | null;
  };
  behaviour?: {
    isLeave: boolean;
    isOvertime: boolean;
    isWorkedHours: boolean;
    isDateRequired: boolean;
    excludeFromSg30hrCheckUnder18: boolean;
    excludeFromLslAverage: boolean;
    isGst: boolean;
    isNovatedLeaseOrOtherBeforeTax: boolean;
    isSalaryPackagingDeduction: boolean;
    printOnPayslipIfZero: boolean;
    excludeFromTaxVariationPowOnly: boolean;
    isStpReportableAllowance: boolean;
  };
  creditorId?: string | null;
}

export interface TenantPayTypeChangeLogEntry {
  sourceChangeId: string;
  sourceType: string;
  sourceId: string;
  sourceCode: string;
  changeType: string;
  beforeJson: string;
  afterJson: string;
  reason: string;
  occurredAtUtc: string;
  affectedRecordType: string;
  affectedRecordId: string;
  affectedRecordName: string;
  affectedPayTypeIds: string[];
  currentReferences: Array<{ referenceType: string; count: number }>;
  reviewState: 'review_required' | 'accepted' | 'dismissed' | 'resolved';
  reviewComment?: string | null;
  reviewedBy?: string | null;
  reviewedAtUtc?: string | null;
}

export interface TenantPayTypeCatalogue {
  rows: TenantPayTypeRow[];
  total: number;
  filterCounts: Array<{ filter: string; value: string; count: number }>;
  regions: Array<{ regionCode: string; count: number }>;
}

export interface TenantPayTypeListRow {
  payTypeListId: string; payTypeListName: string; purpose: string; regionCode?: string;
  status: string; effectiveStatus: string; effectiveStartDate: string; effectiveEndDate?: string;
  membershipCount: number; metadata: PayTypeMetadata; isCustom: boolean;
}
export interface TenantPayTypeListCatalogue { rows: TenantPayTypeListRow[]; total: number; filterCounts: Array<{ filter: string; value: string; count: number }> }
export interface TenantPayTypeChainRow { id: string; sourcePayTypeId: string; targetPayTypeId: string; triggerType: string; effectiveStartDate: string; effectiveEndDate?: string; isCustom: boolean }
export interface TenantPayTypeRegionRow { id: string; payTypeId: string; regionCode: string; effectiveStartDate: string; effectiveEndDate?: string; isCustom: boolean }
export interface TenantLeaveTypeSubTypeRow { id: string; subTypeCode: string; subTypeName: string; effectiveStartDate: string; effectiveEndDate?: string; isCustom: boolean }

export interface BrowseTenantPayTypesQuery {
  search?: string;
  regionCode?: string;
  jurisdictionCode?: string;
  financialYear?: string;
  isCustom?: boolean;
  status?: string;
  payComponentType?: string;
  effectiveDate?: string;
}

export interface ClonePayTypeRequest {
  sourcePayTypeId: string;
  payTypeCode: string;
  payTypeName: string;
  effectiveStartDate: string;
}

export interface UpdateTenantPayTypeRequest {
  payTypeCode: string;
  payTypeName: string;
  groupLabel?: string;
  factor?: number | null;
  status?: 'active' | 'inactive';
  expectedStatus?: 'active' | 'inactive';
  effectiveStartDate?: string;
}
export interface DeactivateTenantPayTypeRequest { reason?: string }
export interface CopyAndCustomisePayTypeRequest {
  sourcePayTypeId: string;
  effectiveStartDate: string;
  initialCustomisation?: UpdateTenantPayTypeRequest;
}
export interface SetOrganisationActivationRequest { isActive: boolean; effectiveStartDate: string; effectiveEndDate?: string }
export interface SetOrganisationRegionsRequest { regionCodes: string[]; effectiveStartDate: string }
export interface OrganisationPayTypeListUsage { payTypeListId: string; payTypeListName: string; purpose: string }
export type BrowseTenantPayTypeGridQuery = Pick<BrowseTenantPayTypesQuery, 'effectiveDate' | 'financialYear' | 'jurisdictionCode' | 'isCustom'>;

export interface TenantPayTypesClient {
  browseOrganisation: (organisationId: string, query?: BrowseTenantPayTypesQuery) => Promise<ApiResponse<TenantPayTypeCatalogue>>;
  getOrganisationDetail: (organisationId: string, payTypeId: string, effectiveDate?: string) => Promise<ApiResponse<TenantPayTypeDetail>>;
  copyAndCustomise: (organisationId: string, request: CopyAndCustomisePayTypeRequest) => Promise<ApiResponse<TenantPayTypeDetail>>;
  setOrganisationActivation: (organisationId: string, sourcePayTypeId: string, request: SetOrganisationActivationRequest) => Promise<ApiResponse<TenantPayTypeRow>>;
  updateOrganisationCopy: (organisationId: string, payTypeId: string, request: Partial<UpdateTenantPayTypeRequest>) => Promise<ApiResponse<TenantPayTypeDetail>>;
  setOrganisationRegions: (organisationId: string, payTypeId: string, request: SetOrganisationRegionsRequest) => Promise<ApiResponse<TenantPayTypeDetail>>;
  organisationWhereUsed: (organisationId: string, payTypeId: string, effectiveDate: string) => Promise<ApiResponse<OrganisationPayTypeListUsage[]>>;
  browse: (query?: BrowseTenantPayTypesQuery) => Promise<ApiResponse<TenantPayTypeCatalogue>>;
  browseLists: (query?: BrowseTenantPayTypesQuery) => Promise<ApiResponse<TenantPayTypeListCatalogue>>;
  browseChains: (query?: BrowseTenantPayTypeGridQuery) => Promise<ApiResponse<TenantPayTypeChainRow[]>>;
  browseRegionApplicabilities: (query?: BrowseTenantPayTypeGridQuery) => Promise<ApiResponse<TenantPayTypeRegionRow[]>>;
  browseLeaveTypeSubTypes: (query?: BrowseTenantPayTypeGridQuery) => Promise<ApiResponse<TenantLeaveTypeSubTypeRow[]>>;
  get: (payTypeId: string) => Promise<ApiResponse<TenantPayTypeDetail>>;
  clone: (request: ClonePayTypeRequest) => Promise<ApiResponse<TenantPayTypeDetail>>;
  update: (payTypeId: string, request: UpdateTenantPayTypeRequest) => Promise<ApiResponse<TenantPayTypeDetail>>;
  deactivate: (payTypeId: string, request: DeactivateTenantPayTypeRequest) => Promise<ApiResponse<TenantPayTypeDetail>>;
  changeLog: (payTypeId: string, financialYear?: string) => Promise<ApiResponse<TenantPayTypeChangeLogEntry[]>>;
  reviewChange: (sourceChangeId: string, affectedRecordType: string, affectedRecordId: string, state: 'accepted' | 'dismissed' | 'resolved', financialYear?: string) => Promise<ApiResponse<TenantPayTypeChangeLogEntry>>;
}

const queryString = <T extends object>(query: T) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => { if (value !== undefined && value !== null && value !== '') params.set(key, String(value)); });
  const value = params.toString();
  return value ? `?${value}` : '';
};

export const createTenantPayTypesClient = (deps: ApiClientDeps): TenantPayTypesClient => {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const base = () => `${deps.getBaseUrl()}/api/PayTypes`;
  const organisationBase = (organisationId: string) => `${deps.getBaseUrl()}/api/organisations/${encodeURIComponent(organisationId)}/pay-types`;
  return {
    browseOrganisation: async (organisationId, query = {}) => parseApiResponse(await fetchImpl(`${organisationBase(organisationId)}${queryString(query)}`)),
    getOrganisationDetail: async (organisationId, payTypeId, effectiveDate) => parseApiResponse(await fetchImpl(`${organisationBase(organisationId)}/${encodeURIComponent(payTypeId)}${queryString({ effectiveDate })}`)),
    copyAndCustomise: async (organisationId, request) => parseApiResponse(await fetchImpl(`${organisationBase(organisationId)}/copy-and-customise`, jsonInit('POST', request))),
    setOrganisationActivation: async (organisationId, sourcePayTypeId, request) => parseApiResponse(await fetchImpl(`${organisationBase(organisationId)}/${encodeURIComponent(sourcePayTypeId)}/activation`, jsonInit('PUT', request))),
    updateOrganisationCopy: async (organisationId, payTypeId, request) => parseApiResponse(await fetchImpl(`${organisationBase(organisationId)}/${encodeURIComponent(payTypeId)}`, jsonInit('PUT', request))),
    setOrganisationRegions: async (organisationId, payTypeId, request) => parseApiResponse(await fetchImpl(`${organisationBase(organisationId)}/${encodeURIComponent(payTypeId)}/regions`, jsonInit('PUT', request))),
    organisationWhereUsed: async (organisationId, payTypeId, effectiveDate) => parseApiResponse(await fetchImpl(`${organisationBase(organisationId)}/${encodeURIComponent(payTypeId)}/pay-type-list-usage${queryString({ effectiveDate })}`)),
    browse: async (query = {}) => parseApiResponse(await fetchImpl(`${base()}${queryString(query)}`)),
    browseLists: async (query = {}) => parseApiResponse(await fetchImpl(`${base()}/list${queryString(query)}`)),
    browseChains: async (query = {}) => parseApiResponse(await fetchImpl(`${base()}/chain${queryString(query)}`)),
    browseRegionApplicabilities: async (query = {}) => parseApiResponse(await fetchImpl(`${base()}/region-applicability${queryString(query)}`)),
    browseLeaveTypeSubTypes: async (query = {}) => parseApiResponse(await fetchImpl(`${base()}/leave-type-sub-type${queryString(query)}`)),
    get: async (payTypeId) => parseApiResponse(await fetchImpl(`${base()}/${encodeURIComponent(payTypeId)}`)),
    clone: async (request) => parseApiResponse(await fetchImpl(`${base()}/clone`, jsonInit('POST', request))),
    update: async (payTypeId, request) => parseApiResponse(await fetchImpl(`${base()}/${encodeURIComponent(payTypeId)}`, jsonInit('PUT', request))),
    deactivate: async (payTypeId, request) => parseApiResponse(await fetchImpl(`${base()}/${encodeURIComponent(payTypeId)}/deactivate`, jsonInit('POST', request))),
    changeLog: async (payTypeId, financialYear) => parseApiResponse(await fetchImpl(`${base()}/change-log${queryString({ payTypeId, financialYear })}`)),
    reviewChange: async (sourceChangeId, affectedRecordType, affectedRecordId, state, financialYear) => parseApiResponse(await fetchImpl(`${base()}/change-log/${encodeURIComponent(sourceChangeId)}/reviews/${encodeURIComponent(affectedRecordType)}/${encodeURIComponent(affectedRecordId)}${queryString({ financialYear })}`, jsonInit('POST', { state }))),
  };
};
